/**
 * Layout màn dọc thích ứng (core-loop 7.1–7.2). Hàm thuần, tính theo CSS px của viewport thật
 * (không dùng khung ảo cố định): nền tràn toàn màn, mọi UI tương tác nằm trong vùng an toàn.
 *
 * Từ trên xuống trong vùng an toàn: HUD → cảnh chiến đấu → thanh skill → bảng ngọc.
 * Bảng được ưu tiên: cảnh co về `sceneMinHeightPx`, thanh skill thu gọn 56; vẫn thiếu thì
 * bảng co theo chiều cao còn lại.
 */

export type Insets = { top: number; right: number; bottom: number; left: number };
export type Rect = { x: number; y: number; width: number; height: number };

export type LayoutConfig = {
  sideGutterPx: number;
  hudHeightPx: number;
  skillBarHeightPx: number;
  sceneMinHeightPx: number;
  boardMaxWidthPx: number;
  pixelIntegerScale: boolean;
};

export type Layout = {
  viewport: { width: number; height: number };
  safe: Rect;
  hud: Rect;
  scene: Rect;
  skillBar: Rect;
  /** Khung bảng (gồm đệm BOARD_PAD mỗi phía theo chiều dọc). */
  board: Rect;
  /** Góc trên-trái của ô (0,0). */
  gridX: number;
  gridY: number;
  cell: number;
  /** Hệ số phóng sprite gốc ORB_SRC_PX. */
  orbScale: number;
  compactSkillBar: boolean;
  landscape: boolean;
};

/** Sprite orb gốc 24 × 24 (core-loop 7.2). */
export const ORB_SRC_PX = 24;
/** Bảng cao `cell × H + 12` → đệm 6 px trên và dưới lưới. */
export const BOARD_PAD = 6;
export const COMPACT_SKILL_BAR_PX = 56;
const MIN_CELL = 16;

export function orbScaleFor(cell: number, integer: boolean): number {
  const raw = (cell - 4) / ORB_SRC_PX;
  const s = Math.floor(raw);
  if (s >= 1) return s;
  return integer ? 1 : Math.max(raw, 0.25);
}

export function computeLayout(
  viewportW: number,
  viewportH: number,
  insets: Insets,
  cfg: LayoutConfig,
  boardW: number,
  boardH: number,
): Layout {
  const vw = Math.max(0, Math.floor(viewportW));
  const vh = Math.max(0, Math.floor(viewportH));
  const safe: Rect = {
    x: insets.left,
    y: insets.top,
    width: Math.max(0, vw - insets.left - insets.right),
    height: Math.max(0, vh - insets.top - insets.bottom),
  };

  let cell = Math.floor(Math.min(safe.width - 2 * cfg.sideGutterPx, cfg.boardMaxWidthPx) / boardW);
  const boardHeightFor = (c: number): number => c * boardH + 2 * BOARD_PAD;

  let skillH = cfg.skillBarHeightPx;
  let compact = false;
  let sceneH = safe.height - cfg.hudHeightPx - skillH - boardHeightFor(cell);
  if (sceneH < cfg.sceneMinHeightPx) {
    skillH = Math.min(skillH, COMPACT_SKILL_BAR_PX);
    compact = true;
    sceneH = safe.height - cfg.hudHeightPx - skillH - boardHeightFor(cell);
  }
  if (sceneH < cfg.sceneMinHeightPx) {
    const room = safe.height - cfg.hudHeightPx - skillH - cfg.sceneMinHeightPx - 2 * BOARD_PAD;
    cell = Math.min(cell, Math.floor(room / boardH));
  }
  cell = Math.max(MIN_CELL, cell);
  const boardHeight = boardHeightFor(cell);
  sceneH = Math.max(0, safe.height - cfg.hudHeightPx - skillH - boardHeight);

  const gridW = cell * boardW;
  const gridX = Math.round(safe.x + (safe.width - gridW) / 2);
  const boardY = safe.y + safe.height - boardHeight;
  const hud: Rect = { x: safe.x, y: safe.y, width: safe.width, height: cfg.hudHeightPx };
  const scene: Rect = { x: safe.x, y: hud.y + hud.height, width: safe.width, height: sceneH };
  const skillBar: Rect = { x: safe.x, y: scene.y + sceneH, width: safe.width, height: skillH };

  return {
    viewport: { width: vw, height: vh },
    safe,
    hud,
    scene,
    skillBar,
    board: { x: gridX, y: boardY, width: gridW, height: boardHeight },
    gridX,
    gridY: boardY + BOARD_PAD,
    cell,
    orbScale: orbScaleFor(cell, cfg.pixelIntegerScale),
    compactSkillBar: compact,
    landscape: vw > vh,
  };
}

/** Ô lưới chứa điểm (CSS px), hoặc null nếu ngoài lưới. */
export function cellAt(
  layout: Pick<Layout, 'gridX' | 'gridY' | 'cell'>,
  boardW: number,
  boardH: number,
  px: number,
  py: number,
): { x: number; y: number } | null {
  const x = Math.floor((px - layout.gridX) / layout.cell);
  const y = Math.floor((py - layout.gridY) / layout.cell);
  if (x < 0 || y < 0 || x >= boardW || y >= boardH) return null;
  return { x, y };
}

/**
 * Ô có vùng chạm (hình tròn bán kính `hitPct`% nửa cạnh ô, tâm giữa ô — core-loop 5.1)
 * chứa điểm, dùng khi kéo để đi chéo không dính ô bên cạnh.
 */
export function cellInHitCircle(
  layout: Pick<Layout, 'gridX' | 'gridY' | 'cell'>,
  boardW: number,
  boardH: number,
  px: number,
  py: number,
  hitPct: number,
): { x: number; y: number } | null {
  const c = cellAt(layout, boardW, boardH, px, py);
  if (!c) return null;
  const cx = layout.gridX + (c.x + 0.5) * layout.cell;
  const cy = layout.gridY + (c.y + 0.5) * layout.cell;
  const r = (hitPct / 100) * (layout.cell / 2);
  const dx = px - cx;
  const dy = py - cy;
  return dx * dx + dy * dy <= r * r ? c : null;
}
